import { useEffect, useState } from 'react';
import { GetShipmentAlerts, GetSingleShipment } from '@/apis/shipment';
import type {
  ShipmentAlertData,
  SingleShipmentDataExtended,
} from '@/apis/types';
import useUnauthorize from '@/hooks/useUnauthorize';
import { FOCUS_REFRESH_MS, keepIfSame, startPolling } from '../polling';

export interface FocusedShipment {
  /** The shipment this data belongs to (never shown for another one). */
  id: string;
  /** Shipment detail, as the dashboard card shows it; null until loaded. */
  detail: Partial<SingleShipmentDataExtended> | null;
  alerts: ShipmentAlertData[] | null;
}

/**
 * The globe spotlight's card for the shipment in focus: its detail and
 * alerts, the same requests the flat monitor makes (the line comes from the
 * page's trip). Null on the overview; a response for a shipment the screen
 * has moved past is dropped. Both refresh while the shipment stays in focus
 * (a paused screen); a failed refresh keeps what is shown.
 */
export const useFocusedShipment = (
  token: string | null | undefined,
  shipmentId: string | null
): FocusedShipment | null => {
  const { unauthorize } = useUnauthorize();
  const [state, setState] = useState<FocusedShipment | null>(null);

  useEffect(() => {
    setState(
      shipmentId ? { id: shipmentId, detail: null, alerts: null } : null
    );
    if (!token || !shipmentId) return undefined;
    // An unchanged answer keeps its object, so the card and pins stay put.
    const update = <K extends 'detail' | 'alerts'>(
      signal: AbortSignal,
      key: K,
      value: FocusedShipment[K]
    ) => {
      if (signal.aborted) return;
      setState((current) =>
        current?.id === shipmentId
          ? { ...current, [key]: keepIfSame(current[key], value) }
          : current
      );
    };

    const stopDetail = startPolling(
      (signal) =>
        GetSingleShipment(token, shipmentId, true)
          .then((res) => {
            const body = res?.data?.data;
            if (body?.is_success && body.data)
              update(
                signal,
                'detail',
                body.data as Partial<SingleShipmentDataExtended>
              );
          })
          .catch((error) => {
            if (!signal.aborted) unauthorize(error);
          }),
      { intervalMs: FOCUS_REFRESH_MS }
    );
    const stopAlerts = startPolling(
      (signal) =>
        GetShipmentAlerts(token, shipmentId)
          .then((res) => {
            const body = res?.data?.data;
            if (body?.is_success)
              update(
                signal,
                'alerts',
                (body.data || []) as ShipmentAlertData[]
              );
          })
          .catch((error) => {
            if (!signal.aborted) unauthorize(error);
          }),
      { intervalMs: FOCUS_REFRESH_MS }
    );
    return () => {
      stopDetail();
      stopAlerts();
    };
  }, [token, shipmentId, unauthorize]);

  return state?.id === shipmentId ? state : null;
};
